<script setup>
import { ref } from "vue";
import { useUserGroupStore } from "@/stores/useUserGroupStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  // Recebe o objeto completo da associação a ser "editada"/visualizada
  association: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["close", "refresh"]);

const store = useUserGroupStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const isLoading = ref(false);
const apiError = ref(null);

// TOAST
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

// Ao concluir com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o emit("close") desmontar o componente.
const hideBackdropForSuccess = ref(false);

// -------- TOAST HELPERS --------
function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, 1500);
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, 1500);
}

// A principal ação de "edição" neste contexto é a exclusão.
async function handleDelete() {
  if (!props.association?.userGroupId) return;

  isLoading.value = true;
  apiError.value = null;

  try {
    await store.deleteAssociationById(props.association.userGroupId);

    showSuccessToast("Vínculo apagado com sucesso.");
    hideBackdropForSuccess.value = true;

    // Espera o usuário ver o toast antes de fechar e atualizar
    setTimeout(() => {
      emit("refresh");
      emit("close");
      isLoading.value = false;
    }, 4000);
  } catch (error) {
    apiError.value =
      error?.response?.data?.message ||
      error.message ||
      "Ocorreu um erro ao apagar o vínculo.";

    console.error("❌ Erro ao apagar vínculo:", error);

    showErrorToast(apiError.value);
    isLoading.value = false;
  }
}
</script>

<template>
  <div v-show="!hideBackdropForSuccess" class="modal-overlay">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">Detalhes do Vínculo</h2>
        <button @click="emit('close')" class="close-button">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <div class="details-grid">
          <div class="detail-item">
            <span class="detail-label">ID do Vínculo</span>
            <span class="detail-value">{{ association?.userGroupId }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Usuário</span>
            <span class="detail-value">
              {{ toTitleCase(association?.userName) }}
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label">E-mail do Usuário</span>
            <span class="detail-value">{{ association?.email }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Grupo</span>
            <span class="detail-value">
              {{ toTitleCase(association?.groupName) }}
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Data da Associação</span>
            <span class="detail-value">
              {{ formatDateTimeForDisplay(association?.addedAt) }}
            </span>
          </div>
        </div>

        <div v-if="apiError" class="api-error-message mt-4">
          {{ apiError }}
        </div>
      </div>

      <div class="modal-footer">
        <button
          @click="emit('close')"
          class="btn btn-secondary"
          :disabled="isLoading"
        >
          Fechar
        </button>
        <button
          @click="handleDelete"
          class="btn btn-danger"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="spinner"></span>
          {{ isLoading ? "Apagando..." : "Apagar Vínculo" }}
        </button>
      </div>
    </div>
  </div>

  <!-- TOAST -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{
                toastType === "error"
                  ? "Não foi possível apagar o vínculo"
                  : "Vínculo apagado com sucesso"
              }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.modal-panel {
  width: 90%;
  max-width: 600px;
}

.close-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  color: var(--text-light);
  transition: all 0.2s;
}
.close-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.details-grid {
  display: grid;
  gap: 1rem;
}
.detail-item {
  background-color: var(--surface-2);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
}
.detail-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-muted);
  margin-bottom: 0.25rem;
}
.detail-value {
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-dark);
}

.api-error-message {
  padding: 0.75rem;
  background-color: var(--danger-hover-bg);
  color: #c92a2a;
  border-radius: 8px;
}

.btn-ghost {
  background: var(--neutral-200);
  color: var(--text-dark);
  border: 1px solid var(--neutral-400);
}

.btn-ghost:not(:disabled):hover {
  background: var(--neutral-300);
  color: var(--text-dark);
}

</style>
